// AddYaku — author a custom data-driven (DSL) yaku and add it to the WORKING catalog. The owner
// composes: id (auto-slugged from the EN name), names (ja/en), kind (normal|yakuman) + han/mult,
// description, an EXAMPLE HAND via the Stage-A <TilePicker>, and a StructuralConstraint TREE via a
// small composer (form / allTilesIn / ctx leaves combined with all/any/none). "Add yaku" calls
// store.addYaku (which appends to store.catalog + enables it + bumps revision) and immediately runs
// Verify (detectYaku on the example with store.catalog) to confirm it fires — proving the round-trip.
import { useMemo, useState } from 'react';
import type { StructuralConstraint, TileId } from '@mj/protocol';
import { useStudioStore, useStudioApi } from '../useStore';
import { TilePicker } from '../render/TilePicker';
import { verifyYaku, type VerifyResult } from '../verify';
import { makeNameOf, makeYakumanName } from '../yakuName';
import {
  slugify,
  buildYakuDef,
  emptyDraft,
  newConstraintNode,
  constraintKindOf,
  CONSTRAINT_KINDS,
  PRESETS,
  type YakuDraft,
  type ConstraintKind,
} from './addYakuLogic';

// ---------------------------------------------------------------------------
// Constraint tree editor — recursive. Leaf nodes edit their fields inline; combinator nodes
// (all/any/none) edit their child list. `onChange` returns the replacement subtree; `onRemove`
// (when present) lets a parent combinator drop this child. Every member of the StructuralConstraint
// union has a leaf editor here so any predicate is authorable visually (rules are EDITABLE DATA).
// ---------------------------------------------------------------------------
const SUITS = ['m', 'p', 's'] as const;
const CTX_FLAGS = ['firstTurn', 'tsumoOnly', 'ronOnly', 'menzenOnly', 'afterKan', 'nonDealer'] as const;
const WAIT_KINDS = ['ryanmen', 'kanchan', 'penchan', 'shanpon', 'tanki', 'none'] as const;
const GROUP_KINDS = ['sequence', 'triplet', 'quad', 'set'] as const;
const HONORS = ['wind', 'dragon'] as const;

// Human-readable label shown in the kind selector for each predicate.
const KIND_LABELS: Record<ConstraintKind, string> = {
  form: 'form',
  allTilesIn: 'allTilesIn',
  groups: 'groups',
  identicalSequences: 'identicalSequences',
  consecutiveTriplets: 'consecutiveTriplets',
  sameSequenceSuits: 'sameSequenceSuits',
  consecutiveSequences: 'consecutiveSequences',
  everyGroupTouches: 'everyGroupTouches',
  pair: 'pair',
  winTile: 'winTile',
  wait: 'wait',
  tilesSubsetOf: 'tilesSubsetOf',
  suitsUsed: 'suitsUsed',
  ctx: 'ctx',
  all: 'all (∧)',
  any: 'any (∨)',
  none: 'none (¬)',
};

// --- small typed field helpers, shared across leaf editors --------------------------------------
const fieldRow: React.CSSProperties = { marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13, alignItems: 'center' };

/** A nullable suit select ('' = any). */
function SuitSelect({ value, onChange }: { value: 'm' | 'p' | 's' | undefined; onChange: (v: 'm' | 'p' | 's' | undefined) => void }) {
  return (
    <label>
      suit{' '}
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? undefined : (e.target.value as 'm' | 'p' | 's'))}>
        <option value="">(any)</option>
        {SUITS.map((s) => (<option key={s} value={s}>{s}</option>))}
      </select>
    </label>
  );
}

/** A nullable honor-class select ('' = any). */
function HonorSelect({ value, onChange }: { value: 'wind' | 'dragon' | undefined; onChange: (v: 'wind' | 'dragon' | undefined) => void }) {
  return (
    <label>
      honor{' '}
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? undefined : (e.target.value as 'wind' | 'dragon'))}>
        <option value="">(any)</option>
        {HONORS.map((h) => (<option key={h} value={h}>{h}</option>))}
      </select>
    </label>
  );
}

/** A nullable tristate boolean select (any / true / false) for predicate flags. */
function TriBool({ label, value, onChange }: { label: string; value: boolean | undefined; onChange: (v: boolean | undefined) => void }) {
  const sel = value === undefined ? '' : value ? 'true' : 'false';
  return (
    <label>
      {label}{' '}
      <select value={sel} onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value === 'true')}>
        <option value="">(any)</option>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    </label>
  );
}

/** An optional integer input ('' clears the field). */
function NumField({ label, value, onChange, min = 0, max = 14 }: { label: string; value: number | undefined; onChange: (v: number | undefined) => void; min?: number; max?: number }) {
  return (
    <label>
      {label}{' '}
      <input
        type="number" min={min} max={max} step={1} style={{ width: 56 }}
        value={value ?? ''}
        // these fields are z.number().int() — truncate so a typed decimal can't produce a schema-invalid def.
        onChange={(e) => onChange(e.target.value === '' ? undefined : Math.trunc(Number(e.target.value)))}
      />
    </label>
  );
}

/** A rank-range [lo, hi] pair editor (1..9). Clearing either field clears the whole range. */
function RankRange({ value, onChange }: { value: [number, number] | undefined; onChange: (v: [number, number] | undefined) => void }) {
  return (
    <label>
      rank{' '}
      <input
        type="number" min={1} max={9} style={{ width: 44 }}
        value={value?.[0] ?? ''}
        onChange={(e) => {
          if (e.target.value === '') return onChange(undefined);
          const lo = Number(e.target.value);
          onChange([lo, value?.[1] ?? lo]);
        }}
      />{' '}–{' '}
      <input
        type="number" min={1} max={9} style={{ width: 44 }}
        value={value?.[1] ?? ''}
        onChange={(e) => {
          if (e.target.value === '') return onChange(undefined);
          const hi = Number(e.target.value);
          onChange([value?.[0] ?? hi, hi]);
        }}
      />
    </label>
  );
}

function ConstraintEditor({
  node,
  onChange,
  onRemove,
  depth = 0,
}: {
  node: StructuralConstraint;
  onChange: (next: StructuralConstraint) => void;
  onRemove?: () => void;
  depth?: number;
}) {
  const kind = constraintKindOf(node);
  const box: React.CSSProperties = {
    border: '1px solid #d6d6d6',
    borderRadius: 6,
    padding: 8,
    marginTop: 6,
    background: depth % 2 === 0 ? '#fff' : '#fafafa',
  };

  return (
    <div style={box}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={kind}
          onChange={(e) => onChange(newConstraintNode(e.target.value as ConstraintKind))}
          aria-label="constraint kind"
        >
          {CONSTRAINT_KINDS.map((k) => (
            <option key={k} value={k}>{KIND_LABELS[k]}</option>
          ))}
        </select>
        {onRemove && (
          <button onClick={onRemove} title="remove this node" style={{ marginLeft: 'auto' }}>
            ✕
          </button>
        )}
      </div>

      {/* ---- form leaf ---- */}
      {'form' in node && (
        <div style={{ marginTop: 6 }}>
          <select value={node.form} onChange={(e) => onChange({ form: e.target.value as 'standard' | 'chiitoi' | 'kokushi' })}>
            <option value="standard">standard</option>
            <option value="chiitoi">chiitoi</option>
            <option value="kokushi">kokushi</option>
          </select>
        </div>
      )}

      {/* ---- allTilesIn leaf ---- */}
      {'allTilesIn' in node && (
        <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13 }}>
          <label>
            suit{' '}
            <select
              value={node.allTilesIn.suit ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                const next = { ...node.allTilesIn };
                if (v === '') delete next.suit;
                else next.suit = v as 'm' | 'p' | 's';
                onChange({ allTilesIn: next });
              }}
            >
              <option value="">(any)</option>
              {SUITS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label>
            rank{' '}
            <input
              type="number" min={1} max={9} style={{ width: 44 }}
              value={node.allTilesIn.rankRange?.[0] ?? ''}
              onChange={(e) => {
                const lo = e.target.value === '' ? undefined : Number(e.target.value);
                const hi = node.allTilesIn.rankRange?.[1] ?? lo ?? 9;
                const next = { ...node.allTilesIn };
                if (lo === undefined) delete next.rankRange;
                else next.rankRange = [lo, hi];
                onChange({ allTilesIn: next });
              }}
            />{' '}–{' '}
            <input
              type="number" min={1} max={9} style={{ width: 44 }}
              value={node.allTilesIn.rankRange?.[1] ?? ''}
              onChange={(e) => {
                const hi = e.target.value === '' ? undefined : Number(e.target.value);
                const lo = node.allTilesIn.rankRange?.[0] ?? hi ?? 1;
                const next = { ...node.allTilesIn };
                if (hi === undefined) delete next.rankRange;
                else next.rankRange = [lo, hi];
                onChange({ allTilesIn: next });
              }}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={node.allTilesIn.honors === true}
              onChange={(e) => {
                const next = { ...node.allTilesIn };
                if (e.target.checked) next.honors = true;
                else delete next.honors;
                onChange({ allTilesIn: next });
              }}
            /> honors
          </label>
          <label>
            <input
              type="checkbox"
              checked={node.allTilesIn.isYaochuu === true}
              onChange={(e) => {
                const next = { ...node.allTilesIn };
                if (e.target.checked) next.isYaochuu = true;
                else delete next.isYaochuu;
                onChange({ allTilesIn: next });
              }}
            /> isYaochuu
          </label>
        </div>
      )}

      {/* ---- ctx leaf ---- */}
      {'ctx' in node && (
        <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13 }}>
          {CTX_FLAGS.map((f) => (
            <label key={f}>
              <input
                type="checkbox"
                checked={(node.ctx as Record<string, boolean | undefined>)[f] === true}
                onChange={(e) => {
                  const next = { ...node.ctx } as Record<string, unknown>;
                  if (e.target.checked) next[f] = true;
                  else delete next[f];
                  onChange({ ctx: next as StructuralConstraint extends { ctx: infer C } ? C : never });
                }}
              /> {f}
            </label>
          ))}
          {/* lastTile (海底/河底) — the one ctx field with engine+schema support that was UI-only via JSON. */}
          <label>lastTile{' '}
            <select
              value={(node.ctx as Record<string, string | undefined>).lastTile ?? ''}
              onChange={(e) => {
                const next = { ...node.ctx } as Record<string, unknown>;
                if (e.target.value === '') delete next.lastTile;
                else next.lastTile = e.target.value;
                onChange({ ctx: next as StructuralConstraint extends { ctx: infer C } ? C : never });
              }}
            >
              <option value="">(any)</option>
              <option value="haitei">haitei (海底)</option>
              <option value="houtei">houtei (河底)</option>
            </select>
          </label>
        </div>
      )}

      {/* ---- groups leaf ---- */}
      {'groups' in node && (
        <div style={fieldRow}>
          <label>
            kind{' '}
            <select
              value={node.groups.kind ?? ''}
              onChange={(e) => {
                const next = { ...node.groups };
                if (e.target.value === '') delete next.kind;
                else next.kind = e.target.value as 'sequence' | 'triplet' | 'quad' | 'set';
                onChange({ groups: next });
              }}
            >
              <option value="">(any)</option>
              {GROUP_KINDS.map((k) => (<option key={k} value={k}>{k}</option>))}
            </select>
          </label>
          <SuitSelect value={node.groups.suit} onChange={(v) => { const n = { ...node.groups }; if (v === undefined) delete n.suit; else n.suit = v; onChange({ groups: n }); }} />
          <HonorSelect value={node.groups.honor} onChange={(v) => { const n = { ...node.groups }; if (v === undefined) delete n.honor; else n.honor = v; onChange({ groups: n }); }} />
          <TriBool label="open" value={node.groups.open} onChange={(v) => { const n = { ...node.groups }; if (v === undefined) delete n.open; else n.open = v; onChange({ groups: n }); }} />
          <NumField label="atLeast" value={node.groups.atLeast} onChange={(v) => { const n = { ...node.groups }; if (v === undefined) delete n.atLeast; else n.atLeast = v; onChange({ groups: n }); }} />
          <NumField label="atMost" value={node.groups.atMost} onChange={(v) => { const n = { ...node.groups }; if (v === undefined) delete n.atMost; else n.atMost = v; onChange({ groups: n }); }} />
        </div>
      )}

      {/* ---- identicalSequences leaf ---- */}
      {'identicalSequences' in node && (
        <div style={fieldRow}>
          <NumField label="atLeast" value={node.identicalSequences.atLeast} onChange={(v) => { const n = { ...node.identicalSequences }; if (v === undefined) delete n.atLeast; else n.atLeast = v; onChange({ identicalSequences: n }); }} />
          <NumField label="pairsAtLeast" value={node.identicalSequences.pairsAtLeast} onChange={(v) => { const n = { ...node.identicalSequences }; if (v === undefined) delete n.pairsAtLeast; else n.pairsAtLeast = v; onChange({ identicalSequences: n }); }} />
        </div>
      )}

      {/* ---- consecutiveTriplets leaf (atLeast required) ---- */}
      {'consecutiveTriplets' in node && (
        <div style={fieldRow}>
          <NumField label="atLeast" min={1} value={node.consecutiveTriplets.atLeast} onChange={(v) => onChange({ consecutiveTriplets: { atLeast: v ?? 1 } })} />
        </div>
      )}

      {/* ---- sameSequenceSuits leaf (atLeast required) ---- */}
      {'sameSequenceSuits' in node && (
        <div style={fieldRow}>
          <NumField label="atLeast" min={1} max={3} value={node.sameSequenceSuits.atLeast} onChange={(v) => onChange({ sameSequenceSuits: { atLeast: v ?? 1 } })} />
        </div>
      )}

      {/* ---- consecutiveSequences leaf (atLeast required) ---- */}
      {'consecutiveSequences' in node && (
        <div style={fieldRow}>
          <NumField label="atLeast" min={1} max={3} value={node.consecutiveSequences.atLeast} onChange={(v) => onChange({ consecutiveSequences: { atLeast: v ?? 1 } })} />
        </div>
      )}

      {/* ---- everyGroupTouches leaf ---- */}
      {'everyGroupTouches' in node && (
        <div style={fieldRow}>
          <TriBool label="terminal" value={node.everyGroupTouches.terminal} onChange={(v) => { const n = { ...node.everyGroupTouches }; if (v === undefined) delete n.terminal; else n.terminal = v; onChange({ everyGroupTouches: n }); }} />
          <TriBool label="honor" value={node.everyGroupTouches.honor} onChange={(v) => { const n = { ...node.everyGroupTouches }; if (v === undefined) delete n.honor; else n.honor = v; onChange({ everyGroupTouches: n }); }} />
          <TriBool label="yaochuu" value={node.everyGroupTouches.yaochuu} onChange={(v) => { const n = { ...node.everyGroupTouches }; if (v === undefined) delete n.yaochuu; else n.yaochuu = v; onChange({ everyGroupTouches: n }); }} />
        </div>
      )}

      {/* ---- pair leaf ---- */}
      {'pair' in node && (
        <div style={fieldRow}>
          <SuitSelect value={node.pair.suit} onChange={(v) => { const n = { ...node.pair }; if (v === undefined) delete n.suit; else n.suit = v; onChange({ pair: n }); }} />
          <HonorSelect value={node.pair.honor} onChange={(v) => { const n = { ...node.pair }; if (v === undefined) delete n.honor; else n.honor = v; onChange({ pair: n }); }} />
          <TriBool label="isYaochuu" value={node.pair.isYaochuu} onChange={(v) => { const n = { ...node.pair }; if (v === undefined) delete n.isYaochuu; else n.isYaochuu = v; onChange({ pair: n }); }} />
          <RankRange value={node.pair.rankRange} onChange={(v) => { const n = { ...node.pair }; if (v === undefined) delete n.rankRange; else n.rankRange = v; onChange({ pair: n }); }} />
        </div>
      )}

      {/* ---- winTile leaf (suit/rank/isYaochuu + an optional tile-list) ---- */}
      {'winTile' in node && (
        <div style={{ marginTop: 6 }}>
          <div style={fieldRow}>
            <SuitSelect value={node.winTile.suit} onChange={(v) => { const n = { ...node.winTile }; if (v === undefined) delete n.suit; else n.suit = v; onChange({ winTile: n }); }} />
            <TriBool label="isYaochuu" value={node.winTile.isYaochuu} onChange={(v) => { const n = { ...node.winTile }; if (v === undefined) delete n.isYaochuu; else n.isYaochuu = v; onChange({ winTile: n }); }} />
            <RankRange value={node.winTile.rankRange} onChange={(v) => { const n = { ...node.winTile }; if (v === undefined) delete n.rankRange; else n.rankRange = v; onChange({ winTile: n }); }} />
          </div>
          <div style={{ marginTop: 6 }}>
            <span style={{ fontSize: 12, color: '#666' }}>winning tile must be one of (empty = any):</span>
            <TilePicker
              value={node.winTile.in ?? []}
              onChange={(next: TileId[]) => { const n = { ...node.winTile }; if (next.length === 0) delete n.in; else n.in = next; onChange({ winTile: n }); }}
              size={24}
            />
          </div>
        </div>
      )}

      {/* ---- wait leaf (multi-select of wait kinds) ---- */}
      {'wait' in node && (
        <div style={fieldRow}>
          {WAIT_KINDS.map((w) => (
            <label key={w}>
              <input
                type="checkbox"
                checked={node.wait.in.includes(w)}
                onChange={(e) => {
                  const set = new Set(node.wait.in);
                  if (e.target.checked) set.add(w); else set.delete(w);
                  onChange({ wait: { in: WAIT_KINDS.filter((k) => set.has(k)) } });
                }}
              /> {w}
            </label>
          ))}
        </div>
      )}

      {/* ---- tilesSubsetOf leaf (the whole hand must be drawn from this tile set) ---- */}
      {'tilesSubsetOf' in node && (
        <div style={{ marginTop: 6 }}>
          <span style={{ fontSize: 12, color: '#666' }}>every tile in the hand must be one of:</span>
          <TilePicker
            value={node.tilesSubsetOf.tiles}
            onChange={(next: TileId[]) => onChange({ tilesSubsetOf: { tiles: next } })}
            size={24}
          />
        </div>
      )}

      {/* ---- suitsUsed leaf ---- */}
      {'suitsUsed' in node && (
        <div style={fieldRow}>
          <label>
            numberSuits{' '}
            <select
              value={node.suitsUsed.numberSuits ?? ''}
              onChange={(e) => { const n = { ...node.suitsUsed }; if (e.target.value === '') delete n.numberSuits; else n.numberSuits = 'one'; onChange({ suitsUsed: n }); }}
            >
              <option value="">(any)</option>
              <option value="one">one</option>
            </select>
          </label>
          <label>
            honors{' '}
            <select
              value={node.suitsUsed.honors ?? ''}
              onChange={(e) => { const n = { ...node.suitsUsed }; if (e.target.value === '') delete n.honors; else n.honors = e.target.value as 'required' | 'forbidden'; onChange({ suitsUsed: n }); }}
            >
              <option value="">(any)</option>
              <option value="required">required</option>
              <option value="forbidden">forbidden</option>
            </select>
          </label>
          <TriBool label="gates" value={node.suitsUsed.gates} onChange={(v) => { const n = { ...node.suitsUsed }; if (v === undefined) delete n.gates; else n.gates = v; onChange({ suitsUsed: n }); }} />
        </div>
      )}

      {/* ---- combinator (all/any/none) ---- */}
      {('all' in node || 'any' in node || 'none' in node) && (
        <Combinator node={node} onChange={onChange} depth={depth} />
      )}
    </div>
  );
}

function Combinator({
  node,
  onChange,
  depth,
}: {
  node: Extract<StructuralConstraint, { all: unknown[] } | { any: unknown[] } | { none: unknown[] }>;
  onChange: (next: StructuralConstraint) => void;
  depth: number;
}) {
  const key = 'all' in node ? 'all' : 'any' in node ? 'any' : 'none';
  const children: StructuralConstraint[] = (node as Record<string, StructuralConstraint[]>)[key]!;
  const replace = (list: StructuralConstraint[]) => onChange({ [key]: list } as unknown as StructuralConstraint);
  return (
    <div style={{ marginTop: 6, paddingLeft: 10, borderLeft: '2px solid #e0e0e0' }}>
      {children.map((c, i) => (
        <ConstraintEditor
          key={i}
          node={c}
          depth={depth + 1}
          onChange={(nx) => replace(children.map((cc, j) => (j === i ? nx : cc)))}
          onRemove={() => replace(children.filter((_, j) => j !== i))}
        />
      ))}
      <button style={{ marginTop: 6 }} onClick={() => replace([...children, newConstraintNode('form')])}>
        + add child
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddYaku panel
// ---------------------------------------------------------------------------
export function AddYaku() {
  const api = useStudioApi();
  const catalog = useStudioStore((s) => s.catalog);
  const nameOf = makeNameOf(catalog);
  const ymName = makeYakumanName(catalog);
  const [draft, setDraft] = useState<YakuDraft>(() => emptyDraft());
  const [verify, setVerify] = useState<VerifyResult | null>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const patch = (p: Partial<YakuDraft>) => setDraft((d) => ({ ...d, ...p }));

  // id auto-slugs from the EN name unless the user has typed an explicit id.
  const [idTouched, setIdTouched] = useState(false);
  const effectiveId = draft.id || (idTouched ? '' : slugify(draft.nameEn));

  const idClash = useMemo(() => catalog.some((d) => d.id === effectiveId), [catalog, effectiveId]);

  const applyPreset = (presetId: string) => {
    const p = PRESETS.find((x) => x.id === presetId);
    if (!p) return;
    setIdTouched(false);
    setVerify(null);
    setMsg('');
    setDraft({ ...emptyDraft(), ...p.draft, id: p.id, constraint: p.constraint } as YakuDraft);
  };

  const onAdd = async () => {
    setMsg('');
    setVerify(null);
    const id = effectiveId;
    if (!id) { setMsg('need a name (or explicit id)'); return; }
    if (!draft.nameEn && !draft.nameJa) { setMsg('need a name'); return; }
    if (draft.exampleHand.length < 14) { setMsg('compose a 14-tile example hand'); return; }
    setBusy(true);
    try {
      const def = buildYakuDef({ ...draft, id });
      api.getState().addYaku(def);
      // Verify against the WORKING catalog (which now contains the new def) so we prove it fires.
      const st = api.getState();
      const r = await verifyYaku(st.engine, def, st.ruleSet, st.catalog);
      setVerify(r);
      setMsg(r.targetFired ? `added ✓ — "${def.name.en || def.name.ja}" fires on its example` : 'added, but it did NOT fire on the example — check the constraint/hand');
    } finally {
      setBusy(false);
    }
  };

  const customCount = catalog.filter((d) => d.source === 'studio:custom').length;

  return (
    <div style={{ maxWidth: 820 }}>
      <p style={{ fontSize: 12, color: '#666', marginTop: 0 }}>
        Author a data-driven (DSL) yaku, give it an example hand, build a condition, then add it. It
        joins the working catalog (enabled), fires in Verify / Example Hand / Sandbox, and survives a
        reload once you Save. {customCount > 0 && <strong>{customCount} custom yaku in catalog.</strong>}
      </p>

      <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 10, marginBottom: 10 }}>
        <legend style={{ fontSize: 12, color: '#555' }}>presets</legend>
        {PRESETS.map((p) => (
          <button key={p.id} onClick={() => applyPreset(p.id)} style={{ marginRight: 6 }}>
            {p.label}
          </button>
        ))}
      </fieldset>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
        <label>
          name (ja)
          <input value={draft.nameJa} onChange={(e) => patch({ nameJa: e.target.value })} style={inp} />
        </label>
        <label>
          name (en)
          <input value={draft.nameEn} onChange={(e) => patch({ nameEn: e.target.value })} style={inp} />
        </label>
        <label>
          id (auto from en name)
          <input
            value={effectiveId}
            onChange={(e) => { setIdTouched(true); patch({ id: slugify(e.target.value) }); }}
            style={{ ...inp, color: idClash ? '#c0202a' : undefined }}
          />
          {idClash && <span style={{ fontSize: 11, color: '#c0202a' }}> id already in catalog (will replace)</span>}
        </label>
        <label>
          kind
          <select value={draft.kind} onChange={(e) => patch({ kind: e.target.value as 'normal' | 'yakuman' })} style={inp}>
            <option value="normal">normal</option>
            <option value="yakuman">yakuman</option>
          </select>
        </label>
        {draft.kind === 'normal' ? (
          <label>
            defaultHan
            <input type="number" min={1} max={13} value={draft.defaultHan} onChange={(e) => patch({ defaultHan: Number(e.target.value) })} style={inp} />
          </label>
        ) : (
          <label>
            yakumanMult
            <input type="number" min={1} max={6} value={draft.yakumanMult} onChange={(e) => patch({ yakumanMult: Number(e.target.value) })} style={inp} />
          </label>
        )}
        <label>
          category
          <input value="localFamous" readOnly style={{ ...inp, background: '#f3f3f3' }} />
        </label>
        <label>
          description (ja)
          <input value={draft.descJa} onChange={(e) => patch({ descJa: e.target.value })} style={inp} />
        </label>
        <label>
          description (en)
          <input value={draft.descEn} onChange={(e) => patch({ descEn: e.target.value })} style={inp} />
        </label>
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
          example hand (14 tiles — last is the winning tile):
        </label>
        <TilePicker value={draft.exampleHand} onChange={(next: TileId[]) => patch({ exampleHand: next })} max={18} size={28} />
      </div>

      <div style={{ marginTop: 12 }}>
        <strong style={{ fontSize: 13 }}>DSL condition</strong>
        <ConstraintEditor node={draft.constraint} onChange={(c) => patch({ constraint: c })} />
        <pre style={{ fontSize: 11, background: '#f6f6f6', padding: 8, borderRadius: 6, marginTop: 8, overflowX: 'auto' }}>
          {JSON.stringify(draft.constraint, null, 2)}
        </pre>
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button onClick={onAdd} disabled={busy} style={{ padding: '6px 14px', fontWeight: 700 }}>
          {busy ? 'adding…' : 'Add yaku'}
        </button>
        {msg && <span style={{ fontSize: 13, color: verify?.targetFired ? '#2b6e2b' : '#a33' }}>{msg}</span>}
      </div>

      {verify && !verify.error && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <span style={{ color: verify.targetFired ? '#2b6e2b' : '#c0202a', fontWeight: 700 }}>
            {verify.targetFired ? '✓ fires' : '✗ did not fire'}
          </span>{' '}
          fired: {[...verify.yakuman.map((y) => `${ymName(y.id, y.mult)}(役満×${y.mult})`), ...verify.fired.map((y) => `${nameOf(y.id)}(${y.han})`)].join(', ') || '—'}
        </div>
      )}
      {verify?.error && <div style={{ marginTop: 8, fontSize: 12, color: '#c0202a' }}>verify error: {verify.error}</div>}
    </div>
  );
}

const inp: React.CSSProperties = { display: 'block', width: '100%', marginTop: 2, padding: '3px 6px' };
